// MealPlan — week grid view (7 days × 3 slots). Tap any cell to add
// or edit. The Plan-the-Week AI flow is a follow-up pass; this turn
// is manual entry only.

import { useState } from "preact/hooks";
import "../../styles/meal-plan.css";

import { Icon } from "../../icons";
import { QueuedDot } from "../../components/queued-dot";
import { useMealPlan } from "../../lib/use-mealplan";
import type { MealSlot } from "../../lib/rpc";
import type { NotifyFn } from "../../components/toast";
import { AddMealSheet } from "./add-meal";
import { PlanTheWeek } from "./plan-the-week";
import { MealPlanEntryDetail } from "./meal-plan-entry-detail";
import { CookMode } from "./cook-mode";

interface MealPlanProps {
  notify: NotifyFn;
  /** When set, clicking a recipe-linked cell calls this to deep-link
   *  into the Recipes section with that recipe open. The parent
   *  (FoodView) handles the section switch + recipe-open plumbing. */
  onOpenRecipe?: (recipeId: number | string) => void;
  /** Household size — threaded into Cook Mode auto-scale picks. */
  householdSize?: number | null;
}

const SLOTS: MealSlot[] = ["breakfast", "lunch", "dinner"];

function localToday(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function dayName(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString([], { weekday: "short" });
}
function dayDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString([], { month: "short", day: "numeric" });
}
function weekLabel(start: string): string {
  const [y, m, d] = start.split("-").map(Number);
  const a = new Date(y, (m || 1) - 1, d || 1);
  const b = new Date(a); b.setDate(a.getDate() + 6);
  const sameMonth = a.getMonth() === b.getMonth();
  const aStr = a.toLocaleDateString([], { month: "short", day: "numeric" });
  const bStr = b.toLocaleDateString([], sameMonth ? { day: "numeric" } : { month: "short", day: "numeric" });
  return `${aStr} – ${bStr}`;
}

export function MealPlan({ notify, onOpenRecipe, householdSize = null }: MealPlanProps) {
  const p = useMealPlan();
  const today = localToday();
  // Per-day customization sheet — opens on recipe-linked cell tap.
  // Replaced the older "tap cell → jump to Recipes" behavior; the
  // recipe view is still one button away via "View recipe" inside
  // the sheet.
  const [editingDay, setEditingDay] = useState<{ date: string; slot: MealSlot } | null>(null);

  const [adding, setAdding] = useState<{ date: string; slot: MealSlot } | null>(null);
  const [planning, setPlanning] = useState(false);
  // Cook Mode launched from a meal-plan entry. Carries the (date, slot)
  // reference so CookMode fetches the OVERLAID effective items via
  // mealplanGetEffectiveItems — preserving the user's swaps/additions
  // for this specific day instead of falling back to the base recipe.
  const [cookingEntry, setCookingEntry] = useState<{ date: string; slot: MealSlot; recipeId: string } | null>(null);
  // Scale picks per-recipe across cook-mode sessions (mirrors what
  // Recipes does — keeps a freshly-bumped scale across consecutive
  // cooks of the same recipe).
  const [scaleByRecipe, setScaleByRecipe] = useState<Record<string, number>>({});

  return (
    <div className="mp-main">
      <div className="mp-toolbar">
        <div className="mp-week-nav">
          <button className="icon-btn" onClick={p.prevWeek} title="Previous week" type="button">
            <Icon name="arrow-left" size={14} />
          </button>
          <button className="icon-btn" onClick={p.nextWeek} title="Next week" type="button">
            <Icon name="arrow-right" size={14} />
          </button>
        </div>
        <div className="mp-week-label">{weekLabel(p.weekStart)}</div>
        <button className="mp-week-today" onClick={p.jumpToToday} type="button">today</button>
        <span className="mp-grow" />
        <button
          className="btn btn-primary"
          onClick={() => setPlanning(true)}
          type="button"
          title="AI: plan the whole week from constraints"
        >
          <Icon name="sparkle" size={14} />
          <span>Plan the week</span>
        </button>
        <button
          className="btn"
          onClick={() => setAdding({ date: today, slot: "dinner" })}
          type="button"
        >
          <Icon name="plus" size={14} />
          <span>Add meal</span>
        </button>
      </div>

      {p.loading
        ? <p style={{ color: "var(--text-dim)", fontFamily: "var(--f-mono)", fontSize: 12 }}>loading…</p>
        : (
          <div className="mp-grid">
            <div className="mp-grid-head">
              <div className="mp-grid-corner" />
              {SLOTS.map((s) => (
                <div key={s} className="mp-slot-label">{s}</div>
              ))}
            </div>
            {p.weekDates.map((iso) => {
              const isToday = iso === today;
              return (
                <div className="mp-row" key={iso}>
                  <div className="mp-day-label" data-today={isToday}>
                    <span className="mp-day-name">{dayName(iso)}</span>
                    <span className="mp-day-date">{dayDate(iso)}</span>
                  </div>
                  {SLOTS.map((slot) => {
                    const entry = p.cell(iso, slot);
                    // Cell click target: recipe-linked entries deep-link
                    // to the Recipes section with the recipe open; empty
                    // and free-text entries open the add/edit sheet.
                    // Editing a recipe-linked meal is done via the
                    // dedicated edit (pencil) button below — that path
                    // stays available, just stops being the default.
                    const handleCellClick = () => {
                      if (entry?.recipeId) {
                        // Recipe-linked cells open the per-day
                        // customization sheet. The sheet has a "View
                        // recipe" button that re-routes to onOpenRecipe
                        // for users who just wanted the canonical view.
                        setEditingDay({ date: iso, slot });
                      } else {
                        setAdding({ date: iso, slot });
                      }
                    };
                    return (
                      <button
                        key={slot}
                        className="mp-cell"
                        data-empty={!entry}
                        data-today={isToday}
                        data-slot-label={slot}
                        data-recipe={!!entry?.recipeId}
                        onClick={handleCellClick}
                        type="button"
                      >
                        {entry ? (
                          <>
                            {entry.recipeId && (
                              <span className="mp-cell-recipe-tag">recipe</span>
                            )}
                            {entry.substitutions && (
                              <span className="mp-cell-modified-tag" title="Customized for this day">modified</span>
                            )}
                            <span className="mp-cell-value">
                              {entry.value}
                              <QueuedDot queued={entry._queued} />
                            </span>
                            {entry.recipeId && (
                              <button
                                className="mp-cell-edit"
                                title="Edit meal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAdding({ date: iso, slot });
                                }}
                                type="button"
                              >
                                <Icon name="edit" size={12} />
                              </button>
                            )}
                            <button
                              className="mp-cell-clear"
                              title="Clear"
                              onClick={(e) => {
                                e.stopPropagation();
                                p.clear(iso, slot);
                              }}
                              type="button"
                            >
                              <Icon name="x" size={12} />
                            </button>
                          </>
                        ) : (
                          <span className="mp-cell-value">empty</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )
      }

      {adding && (
        <AddMealSheet
          open={true}
          onClose={() => setAdding(null)}
          onSave={async (args) => {
            await p.set(args);
            notify("Added to plan");
          }}
          weekDates={p.weekDates}
          prefillDate={adding.date}
          prefillSlot={adding.slot}
        />
      )}

      {editingDay && (
        <MealPlanEntryDetail
          date={editingDay.date}
          slot={editingDay.slot}
          onClose={() => { setEditingDay(null); p.refresh(); }}
          onOpenRecipe={onOpenRecipe ? (id) => { setEditingDay(null); onOpenRecipe(id); } : undefined}
          onCook={(recipeId) => {
            // Stash the recipe id alongside (date, slot) so CookMode
            // can fetch the recipe + the meal-plan overlay both at
            // once. Closing the customization sheet here is intentional
            // — we don't want it bouncing back into view after Cook
            // Mode closes.
            const id = String(recipeId);
            setEditingDay(null);
            setCookingEntry({ date: editingDay.date, slot: editingDay.slot, recipeId: id });
          }}
          notify={notify}
        />
      )}

      {cookingEntry && (
        <CookMode
          recipeId={cookingEntry.recipeId}
          entryRef={{ date: cookingEntry.date, slot: cookingEntry.slot }}
          scale={scaleByRecipe[cookingEntry.recipeId] ?? 1}
          onScaleChange={(s) => setScaleByRecipe((prev) => ({ ...prev, [cookingEntry.recipeId]: s }))}
          onClose={() => setCookingEntry(null)}
          householdSize={householdSize}
          notify={notify}
        />
      )}

      <PlanTheWeek
        open={planning}
        onClose={() => setPlanning(false)}
        weekStart={p.weekStart}
        weekDates={p.weekDates}
        existing={p.entries}
        notify={notify}
        onApplied={p.refresh}
      />
    </div>
  );
}
